import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/useAuth";
import { Mail, Lock, User, AlertCircle, Eye, EyeOff, CheckCircle, RefreshCw, Loader2 } from "lucide-react";
import { ThemeLoader } from "../../shared/components/ThemeLoader";
import { OrchyLogo } from "../ui/OrchyLogo";
import { SEO } from "../SEO";
import { createFirebaseAccount, checkEmailVerified, auth } from "../../lib/firebase";
import { deleteUser } from "firebase/auth";

const API_BASE_URL = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { exchangeFirebaseToken } = useAuth();

  // ── Step state ────────────────────────────────────────────────────────────
  const [step, setStep] = useState<"form" | "verify">("form");

  // ── Form fields ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    email: "", password: "", confirmPassword: "", firstName: "", lastName: "",
  });
  const [showPassword,        setShowPassword]        = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    firstName?: string; lastName?: string; email?: string;
    password?: string; confirmPassword?: string;
  }>({});

  // ── Verification state ────────────────────────────────────────────────────
  const [verifyError,  setVerifyError]  = useState("");
  const [checking,     setChecking]     = useState(false);
  const [resending,    setResending]    = useState(false);
  // ── Shared ────────────────────────────────────────────────────────────────
  const [error,       setError]       = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-detect verification: poll Firebase every 3 s so the user never has
  // to click "I've verified" — and a page refresh still works.
  useEffect(() => {
    if (step !== "verify") return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const verified = await checkEmailVerified();
        if (verified && !cancelled) {
          try { await exchangeFirebaseToken(); } catch { /* onAuthStateChanged retries */ }
          if (!cancelled) navigate("/login?signup=success");
          return;
        }
      } catch { /* ignore transient errors, retry next tick */ }
      if (!cancelled) setTimeout(poll, 3000);
    };

    const first = setTimeout(poll, 3000);
    return () => { cancelled = true; clearTimeout(first); };
  }, [step]);

  // ── Validators ────────────────────────────────────────────────────────────
  const validateFirstName = (v: string) => !v ? "First name is required" : v.length < 2 ? "Min 2 characters" : null;
  const validateLastName  = (v: string) => !v ? "Last name is required"  : v.length < 2 ? "Min 2 characters" : null;
  const validateEmail     = (v: string) => !v ? "Email is required" : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "Invalid email" : null;
  const validatePassword  = (v: string) => {
    if (!v)                        return "Password is required";
    if (v.length < 8)              return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(v))          return "Must contain an uppercase letter";
    if (!/[a-z]/.test(v))          return "Must contain a lowercase letter";
    if (!/[0-9]/.test(v))          return "Must contain a number";
    if (!/[^A-Za-z0-9]/.test(v))   return "Must contain a special character";
    return null;
  };
  const validateConfirm = (pw: string, cpw: string) =>
    !cpw ? "Please confirm your password" : pw !== cpw ? "Passwords do not match" : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError("");
    let err: string | null = null;
    if      (name === "firstName")       err = validateFirstName(value);
    else if (name === "lastName")        err = validateLastName(value);
    else if (name === "email")           err = validateEmail(value);
    else if (name === "password") {
      err = validatePassword(value);
      if (formData.confirmPassword) {
        setFieldErrors(prev => ({ ...prev, password: err || undefined, confirmPassword: validateConfirm(value, formData.confirmPassword) || undefined }));
        return;
      }
    } else if (name === "confirmPassword") err = validateConfirm(formData.password, value);
    setFieldErrors(prev => ({ ...prev, [name]: err || undefined }));
  };

  // Step 1: validate → check uniqueness → create Firebase account → send verification email
  const handleSendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const errs = {
      firstName:       validateFirstName(formData.firstName),
      lastName:        validateLastName(formData.lastName),
      email:           validateEmail(formData.email),
      password:        validatePassword(formData.password),
      confirmPassword: validateConfirm(formData.password, formData.confirmPassword),
    };
    if (Object.values(errs).some(Boolean)) {
      setFieldErrors({ firstName: errs.firstName || undefined, lastName: errs.lastName || undefined, email: errs.email || undefined, password: errs.password || undefined, confirmPassword: errs.confirmPassword || undefined });
      return;
    }
    setIsSubmitting(true);
    try {
      // Check uniqueness against our backend first
      const check = await fetch(`${API_BASE_URL}/auth/send-otp`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email.toLowerCase() }),
      });
      if (!check.ok) {
        const d = await check.json() as { error?: string };
        setError(d.error ?? "This email is already registered.");
        return;
      }
      // Create Firebase user + send verification email
      await createFirebaseAccount(
        formData.email,
        formData.password,
        `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      );
      setStep("verify");
    } catch (err: any) {
      const code: string = err?.code ?? "";
      if (code === "auth/email-already-in-use") setError("An account with this email already exists.");
      else if (code === "auth/weak-password")   setError("Password is too weak.");
      else setError(err.message || "Failed to create account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: confirm Firebase email verification, then redirect to login to provision the Orchy account
  const handleConfirmVerified = async () => {
    setVerifyError("");
    setChecking(true);
    try {
      const verified = await checkEmailVerified();
      if (!verified) {
        setVerifyError("Email not verified yet. Check your inbox and click the link, then try again.");
        return;
      }
      // MongoDB account is created on first sign-in via the login form
      navigate("/login?signup=success");
    } catch (err: any) {
      setVerifyError(err.message || "Verification check failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true); setVerifyError("");
    try {
      const user = auth.currentUser;
      if (user) {
        const { sendEmailVerification } = await import("firebase/auth");
        const appOrigin = (import.meta.env.VITE_APP_URL as string | undefined)?.replace(/\/$/, "")
          ?? window.location.origin.replace(/^(https?:\/\/)www\./, "$1");
        await sendEmailVerification(user, { url: `${appOrigin}/auth/verify-email` });
      }
    } finally {
      setResending(false);
    }
  };

  const handleBackToForm = async () => {
    // Clean up the unverified Firebase account so the user can re-submit
    const user = auth.currentUser;
    if (user) { try { await deleteUser(user); } catch { /* ignore */ } }
    setStep("form");
    setVerifyError("");
  };

  return (
    <div className="min-h-screen bg-base flex items-center justify-center p-4">
      <SEO title="Create Account" description="Create your Orchy account." noIndex />
      <main id="main-content" className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <OrchyLogo size={28} />
            <h1 className="text-3xl font-bold text-text">Orchy</h1>
          </div>
          <p className="text-secondary">Workflow Orchestration Platform</p>
        </div>

        <div className="bg-surface rounded-lg border border-edge shadow-lg p-8">

          {/* ── Step indicator ── */}
          <div className="flex items-center gap-2 mb-6">
            {(["form", "verify"] as const).map((s, i) => (
              <React.Fragment key={s}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                  step === s ? "bg-brand text-on-primary" :
                  (i === 0 && step === "verify") ? "bg-brand/20 text-brand" : "bg-card text-secondary"
                }`}>
                  {i === 0 && step === "verify" ? <CheckCircle size={14} /> : i + 1}
                </div>
                {i === 0 && <div className={`flex-1 h-0.5 rounded ${step === "verify" ? "bg-brand/40" : "bg-edge"}`} />}
              </React.Fragment>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-text mb-6">
            {step === "form" ? "Create Account" : "Verify your email"}
          </h2>

          {error && (
            <div role="alert" className="mb-4 p-3 bg-error/10 border border-error rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error shrink-0 mt-0.5" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {/* ── Step 1: Registration form ── */}
          {step === "form" && (
            <form onSubmit={handleSendVerification} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {(["firstName", "lastName"] as const).map(field => (
                  <div key={field}>
                    <label htmlFor={field} className="block text-sm font-medium text-text mb-2">
                      {field === "firstName" ? "First Name" : "Last Name"} <span className="text-error">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-5 h-5 text-secondary" />
                      <input id={field} type="text" name={field}
                        value={formData[field]} onChange={handleChange}
                        placeholder={field === "firstName" ? "John" : "Doe"} disabled={isSubmitting}
                        className={`w-full bg-base border rounded-lg pl-10 pr-3 py-2.5 text-text placeholder-secondary focus:outline-none focus:ring-1 focus:border-transparent disabled:opacity-50 transition ${fieldErrors[field] ? "border-error focus:ring-error" : "border-edge focus:ring-brand"}`}
                      />
                    </div>
                    {fieldErrors[field] && <p className="text-xs text-error mt-1">{fieldErrors[field]}</p>}
                  </div>
                ))}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text mb-2">Email <span className="text-error">*</span></label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-secondary" />
                  <input id="email" type="email" name="email" value={formData.email} onChange={handleChange}
                    placeholder="you@example.com" disabled={isSubmitting}
                    className={`w-full bg-base border rounded-lg pl-10 pr-4 py-2.5 text-text placeholder-secondary focus:outline-none focus:ring-1 focus:border-transparent disabled:opacity-50 transition ${fieldErrors.email ? "border-error focus:ring-error" : "border-edge focus:ring-brand"}`}
                  />
                </div>
                {fieldErrors.email && <p className="text-xs text-error mt-1">{fieldErrors.email}</p>}
              </div>

              {(["password", "confirmPassword"] as const).map(field => {
                const isConfirm = field === "confirmPassword";
                const show = isConfirm ? showConfirmPassword : showPassword;
                const toggle = () => isConfirm ? setShowConfirmPassword(p => !p) : setShowPassword(p => !p);
                return (
                  <div key={field}>
                    <label htmlFor={field} className="block text-sm font-medium text-text mb-2">
                      {isConfirm ? "Confirm Password" : "Password"} <span className="text-error">*</span>
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-secondary" />
                      <input id={field} type={show ? "text" : "password"} name={field}
                        value={formData[field]} onChange={handleChange}
                        placeholder="••••••••" disabled={isSubmitting}
                        className={`w-full bg-base border rounded-lg pl-10 pr-10 py-2.5 text-text placeholder-secondary focus:outline-none focus:ring-1 focus:border-transparent disabled:opacity-50 transition ${fieldErrors[field] ? "border-error focus:ring-error" : "border-edge focus:ring-brand"}`}
                      />
                      <button type="button" onClick={toggle} disabled={isSubmitting}
                        className="absolute right-3 top-3 text-secondary hover:text-text disabled:opacity-50 transition">
                        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {fieldErrors[field] && <p className="text-xs text-error mt-1">{fieldErrors[field]}</p>}
                    {!isConfirm && !fieldErrors.password && formData.password && (
                      <p className="text-xs text-secondary mt-1">Min 8 chars · uppercase · lowercase · number · special character</p>
                    )}
                  </div>
                );
              })}

              <button type="submit" disabled={isSubmitting}
                className="w-full bg-brand hover:bg-brand-dim disabled:bg-edge disabled:text-secondary text-on-primary font-semibold py-2.5 rounded-lg transition flex items-center justify-center gap-2 mt-2">
                {isSubmitting && <ThemeLoader size={16} />}
                {isSubmitting ? "Sending verification email…" : "Create Account & Verify Email"}
              </button>
            </form>
          )}

          {/* ── Step 2: Email verification ── */}
          {step === "verify" && (
            <div className="space-y-5">
              {/* Success banner */}
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Verification email sent!</p>
                  <p className="text-xs text-secondary mt-0.5">
                    A verification link was sent to{" "}
                    <span className="font-mono font-semibold">{formData.email}</span>.
                    Please verify your account by clicking the link in that email, then return here.
                  </p>
                </div>
              </div>

              {verifyError && (
                <div className="p-3 bg-error/10 border border-error rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-error shrink-0 mt-0.5" />
                  <p className="text-error text-xs">{verifyError}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleConfirmVerified}
                disabled={checking}
                className="w-full bg-brand hover:bg-brand-dim disabled:bg-edge disabled:text-secondary text-on-primary font-semibold py-2.5 rounded-lg transition flex items-center justify-center gap-2"
              >
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {checking ? "Checking…" : "I've verified my email"}
              </button>

              <div className="flex items-center justify-between text-xs text-secondary">
                <button type="button" onClick={handleBackToForm} className="hover:text-text transition">
                  ← Change details
                </button>
                <button type="button" onClick={handleResendVerification} disabled={resending}
                  className="flex items-center gap-1 hover:text-text disabled:opacity-50 transition">
                  <RefreshCw size={11} className={resending ? "animate-spin" : ""} />
                  {resending ? "Sending…" : "Resend email"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 pt-5 border-t border-edge text-center">
            <p className="text-sm text-secondary">
              Already have an account?{" "}
              <Link to="/login" className="text-brand hover:text-brand-light font-semibold transition">Sign in</Link>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center text-secondary text-sm">
          <p>&copy; {new Date().getFullYear()} Orchy. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

